// Updated: 2026-08-14
import type {
  UnreadNotificationCountResponse,
  MarkNotificationReadResponse,
  PaginatedNotificationsResponse,
  NotificationCategory,
} from '@mrb91002/shared-types'

import { secureFetch, SECURE_API_BASE } from './helpers'

/**
 * Get per-category unread notification counts for badge and nav
 * GET /notifications/unread-count
 *
 * `opts.signal` is optional request cancellation forwarded from a React Query `queryFn`, spread
 * conditionally so a caller that omits it makes a byte-identical request. `secureFetch`'s `RequestInit`
 * already accepts `signal` and threads it into both the initial request and the 401 retry, so nothing
 * about the token-refresh path changes (react-query-notifications, Req 8.1).
 */
export async function getUnreadNotificationCount(
  idToken: string,
  opts?: { signal?: AbortSignal },
): Promise<UnreadNotificationCountResponse> {
  return secureFetch(`${SECURE_API_BASE}/notifications/unread-count`, idToken, {
    method: 'GET',
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
}

/**
 * Fetch paginated notifications — all or by category
 * GET /notifications
 * GET /notifications?category=patterns
 * GET /notifications?category=patterns&lastKey=...
 *
 * THE `category && category !== 'all'` GUARD IS LOAD-BEARING and must not be "simplified": it is why
 * `fetchNotificationsPaginated(null, token)` (the nav bell) and
 * `fetchNotificationsPaginated(null, token, 'all')` (the Notification Center's "All" tab) put the
 * IDENTICAL URL on the wire, which in turn is what lets both surfaces share one React Query cache entry
 * and one request (react-query-notifications, design § D3).
 *
 * `opts.signal` — same additive forward as above.
 */
export async function fetchNotificationsPaginated(
  lastKey: string | null,
  idToken: string,
  category?: NotificationCategory,
  opts?: { signal?: AbortSignal },
): Promise<PaginatedNotificationsResponse> {
  const params = new URLSearchParams()
  if (category && category !== 'all') params.set('category', category)
  if (lastKey) params.set('lastKey', lastKey)
  const qs = params.toString()
  const url = qs ? `${SECURE_API_BASE}/notifications?${qs}` : `${SECURE_API_BASE}/notifications`
  return secureFetch(url, idToken, {
    method: 'GET',
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
}

/**
 * Mark a single notification as read
 * PATCH /notifications/{id}/read
 * Body: { id, category, created_at_ms }
 *
 * NO `signal` parameter, deliberately: React Query does not abort mutations, so the parameter would be
 * untested surface area (react-query-notifications, Req 8.2).
 */
export async function markNotificationRead(
  notificationId: string,
  category: Exclude<NotificationCategory, 'all'>,
  created_at_ms: number,
  idToken: string,
): Promise<MarkNotificationReadResponse> {
  return secureFetch<MarkNotificationReadResponse>(`${SECURE_API_BASE}/notifications/${encodeURIComponent(notificationId)}/read`, idToken, {
    method: 'PATCH',
    body: JSON.stringify({ id: notificationId, category, created_at_ms }),
  })
}

/**
 * Delete a notification
 * DELETE /notifications/{id}?category=patterns&created_at_ms=1773022093765
 *
 * NO `signal` parameter — same reason as `markNotificationRead`.
 */
export async function deleteNotification(
  id: string,
  category: Exclude<NotificationCategory, 'all'>,
  created_at_ms: number,
  idToken: string,
): Promise<void> {
  const params = new URLSearchParams({ category, created_at_ms: String(created_at_ms) })
  return secureFetch(
    `${SECURE_API_BASE}/notifications/${encodeURIComponent(id)}?${params.toString()}`,
    idToken,
    { method: 'DELETE' },
  )
}
